<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 v-if="status" class="mt-0">{{ title }}</h1>

      <div>
        <el-tooltip :content="loadedAtFormatted">
          <span class="text-muted mr-3" v-t="{
            path: 'views.station.loadedAt',
            args: { loadedAt: loadedAtFromNow },
          }"></span>
        </el-tooltip>

        <el-button class="mr-2" @click="refresh" v-t="'common.refresh'"/>

        <el-select
          class="mr-2"
          v-if="isCommissioner"
          v-model="manufacturerId"
          @change="onManufacturerIdChanged"
        >
          <el-option v-for="manufacturer in manufacturers"
            :key="manufacturer.id"
            :label="manufacturer.name"
            :value="manufacturer.id"
          />
        </el-select>

        <el-button type="primary" @click="onScanClick" v-t="'views.station.scan'"></el-button>
      </div>
    </div>

    <scan-dialog
      :visible.sync="scanDialogVisible"
      :manufacturing-item-id="scanDialogManufacturingItemId"
      :status="status"
      @pushed:status="onPushedStatus"
    />

    <el-tabs v-if="isStationValid" v-model="activeTab">
      <el-tab-pane :label="$t('views.station.todo')" name="todo">
        <station-grid
          v-if="status"
          :key="`status-${status.id}-todo`"
          ref="todoGrid"
          :station="station"
          :next-status="status"
          :filter-value="todoFilterValue"
          :sort-by="{priority: 'desc', 'planWeek': 'asc'}"
          @click:push-status="onScanDialogPushStatusClick($event)"
          @loaded="onLoaded"
        />
      </el-tab-pane>

      <el-tab-pane :label="$t('views.station.done')" name="done">
        <station-grid
          v-if="status"
          :key="`status-${status.id}-done`"
          ref="doneGrid"
          :station="station"
          :show-status="status"
          :filter-value="doneFilterValue"
          :sort-by="{statusOccurredAt: 'desc'}"
          @loaded="onLoaded"
        />
      </el-tab-pane>
    </el-tabs>

    <p v-else>{{ invalidStationMessage }}</p>
  </div>
</template>

<script>
import { get } from 'lodash';
import moment from 'moment';
import { mapGetters, mapState } from 'vuex';
import { getManufacturer, getManufacturerStatuses } from '@/store';
import ScanDialog from '@/components/ScanDialog.vue';
import StationGrid from '@/components/StationGrid.vue';
import Clock from '@/lib/clock';
import { previous, translateValue } from '@/lib/util';

export default {
  name: 'Station',
  components: {
    ScanDialog,
    StationGrid,
  },
  data() {
    return {
      clock: new Clock(5000),
      activeTab: 'todo',
      manufacturerId: NaN,
      scanDialogVisible: false,
      scanDialogManufacturingItemId: NaN,
      loadedAt: new Date(),
      reloadInterval: moment.duration(5, 'minutes')
        .valueOf(),
    };
  },
  computed: {
    ...mapState([
      'manufacturers',
      'stationManufacturerId',
      'statuses',
      'user',
    ]),
    ...mapGetters(['isCommissioner']),
    title() {
      return translateValue('status', 'station', this.status.station);
    },
    invalidStationMessage() {
      const manufacturerName = get(this.manufacturer, 'name') || 'N/A';
      return this.$t('views.station.invalidStation', { manufacturerName });
    },
    station() {
      return this.$route.params.station;
    },
    isStationValid() {
      return this.manufacturerStatuses.includes(this.status);
    },
    status() {
      return this.statuses.find(status => status.station === this.station);
    },
    previousStatus() {
      return previous(this.manufacturerStatuses, this.status) || null;
    },
    manufacturerStatuses() {
      if (Number.isNaN(this.manufacturerId)) return [];
      return getManufacturerStatuses(this.$store.state, this.manufacturerId);
    },
    manufacturer() {
      return getManufacturer(this.$store.state, this.manufacturerId);
    },
    loadedAtFormatted() {
      return moment(this.loadedAt)
        .format('L LTS');
    },
    loadedAtFromNow() {
      return moment(this.loadedAt)
        .from(this.clock.now);
    },
    todoFilterValue() {
      if (!this.manufacturerId || !this.previousStatus) return null;

      return [
        ['manufacturerId', '=', this.manufacturerId],
        'and',
        ['statusId', '=', previous(this.manufacturerStatuses, this.status).id],
      ];
    },
    doneFilterValue() {
      if (!this.manufacturerId) return null;

      return [
        ['manufacturerId', '=', this.manufacturerId],
        'and',
        ['statusId', '=', this.status.id],
      ];
    },
  },
  watch: {
    activeTab() {
      this.refresh();
    },
    // eslint-disable-next-line object-shorthand
    'clock.now'(value) {
      const lastReload = moment(value)
        .diff(this.loadedAt)
        .valueOf();

      if (lastReload >= this.reloadInterval) {
        this.refresh();
      }
    },
  },
  methods: {
    refresh() {
      if (this.activeTab === 'todo') {
        this.$refs.todoGrid.refresh();
      }

      if (this.activeTab === 'done') {
        this.$refs.doneGrid.refresh();
      }
    },
    showScanDialog(manufacturingItemId = NaN) {
      this.scanDialogManufacturingItemId = manufacturingItemId;
      this.scanDialogVisible = true;
    },
    onManufacturerIdChanged(value) {
      this.$store.commit('set', { stationManufacturerId: value });
    },
    onScanClick() {
      this.showScanDialog();
    },
    onScanDialogPushStatusClick(event) {
      this.showScanDialog(event.row.key);
    },
    onPushedStatus() {
      this.refresh();
    },
    onLoaded() {
      this.clock.refresh();
      this.loadedAt = this.clock.now;
    },
  },
  async mounted() {
    this.manufacturerId = this.stationManufacturerId || this.user.manufacturerId;
    this.clock.start();
  },
  beforeDestroy() {
    this.clock.stop();
  },
};
</script>

<style lang="sass" scoped>
</style>
